import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from "react";

const SoundContext = createContext(null);
export const useSound = () => useContext(SoundContext);

// Web Audio synthesizer — casino card game sounds, no external files
function createAudioEngine() {
  let ctx = null;
  const getCtx = () => {
    if (!ctx || ctx.state === "closed") ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    return ctx;
  };

  const playTone = (freq, duration, type = "sine", vol = 0.3, attack = 0.01, decay = 0.1) => {
    const c = getCtx();
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, c.currentTime);
    gain.gain.setValueAtTime(0, c.currentTime);
    gain.gain.linearRampToValueAtTime(vol, c.currentTime + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
    osc.connect(gain).connect(c.destination);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + duration + 0.05);
  };

  const playNoise = (duration, vol = 0.15, filterFreq = 3000, filterType = "lowpass") => {
    const c = getCtx();
    const bufferSize = c.sampleRate * duration;
    const buffer = c.createBuffer(1, bufferSize, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const source = c.createBufferSource();
    source.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.setValueAtTime(filterFreq, c.currentTime);
    const gain = c.createGain();
    gain.gain.setValueAtTime(vol, c.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + duration);
    source.connect(filter).connect(gain).connect(c.destination);
    source.start(c.currentTime);
  };

  return {
    // Card shuffle — rustling noise burst
    shuffle() {
      playNoise(0.4, 0.12, 4000, "bandpass");
      setTimeout(() => playNoise(0.3, 0.1, 5000, "bandpass"), 150);
      setTimeout(() => playNoise(0.2, 0.08, 3500, "bandpass"), 300);
    },

    // Card deal/draw — crisp snap
    cardDraw() {
      playNoise(0.06, 0.2, 6000, "highpass");
      playTone(800, 0.08, "sine", 0.15, 0.005, 0.03);
    },

    // Coin/chip click — buy transaction
    coinClick() {
      playTone(1200, 0.12, "sine", 0.25, 0.005, 0.05);
      setTimeout(() => playTone(1600, 0.08, "sine", 0.15, 0.005, 0.03), 60);
    },

    // Card slide — sell action
    cardSlide() {
      const c = getCtx();
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(400, c.currentTime);
      osc.frequency.exponentialRampToValueAtTime(200, c.currentTime + 0.15);
      gain.gain.setValueAtTime(0.15, c.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.15);
      osc.connect(gain).connect(c.destination);
      osc.start(); osc.stop(c.currentTime + 0.2);
      playNoise(0.08, 0.1, 2000, "lowpass");
    },

    // Hold — soft thud
    holdThud() {
      playTone(150, 0.15, "sine", 0.2, 0.01, 0.08);
      playNoise(0.05, 0.06, 800, "lowpass");
    },

    // Swoosh — turn transition
    swoosh() {
      const c = getCtx();
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(200, c.currentTime);
      osc.frequency.exponentialRampToValueAtTime(600, c.currentTime + 0.25);
      gain.gain.setValueAtTime(0.12, c.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + 0.3);
      osc.connect(gain).connect(c.destination);
      osc.start(); osc.stop(c.currentTime + 0.35);
      playNoise(0.2, 0.06, 2000, "bandpass");
    },

    // Event alert — two-tone bell
    eventBell() {
      playTone(880, 0.2, "sine", 0.2, 0.005, 0.1);
      setTimeout(() => playTone(1100, 0.25, "sine", 0.18, 0.005, 0.12), 120);
    },

    // Market Melee — dramatic impact
    meleeImpact() {
      playTone(80, 0.5, "sawtooth", 0.3, 0.01, 0.2);
      playNoise(0.3, 0.2, 500, "lowpass");
      setTimeout(() => playTone(60, 0.4, "square", 0.15, 0.01, 0.15), 200);
    },

    // Win fanfare — ascending chord
    winFanfare() {
      [523, 659, 784, 1047].forEach((freq, i) => {
        setTimeout(() => playTone(freq, 0.4, "sine", 0.2, 0.01, 0.2), i * 120);
      });
      setTimeout(() => playNoise(0.3, 0.05, 8000, "highpass"), 500);
    },

    // Fail/negative — descending buzz
    failBuzz() {
      playTone(300, 0.25, "sawtooth", 0.15, 0.01, 0.1);
      setTimeout(() => playTone(200, 0.3, "sawtooth", 0.12, 0.01, 0.15), 100);
    },

    // Button click — subtle tap
    click() {
      playTone(600, 0.04, "sine", 0.12, 0.003, 0.02);
    },

    // Lock sound — metallic clank
    lockSound() {
      playTone(400, 0.1, "square", 0.15, 0.005, 0.05);
      setTimeout(() => playTone(300, 0.12, "square", 0.1, 0.005, 0.05), 80);
    },

    // Play card from hand — dramatic reveal
    playCard() {
      playNoise(0.05, 0.15, 5000, "highpass");
      playTone(500, 0.15, "triangle", 0.2, 0.005, 0.08);
      setTimeout(() => playTone(700, 0.12, "triangle", 0.15, 0.005, 0.06), 80);
    },
  };
}

// localStorage access is wrapped in try/catch because Safari Private
// Browsing, GDPR-strict mode, and over-quota states all throw on
// `getItem` / `setItem`. An audio preference disappearing across
// sessions is a smaller cost than the SoundProvider crashing the app
// on boot, which is what unwrapped access does.
function readBool(key, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return fallback;
    return v === "true";
  } catch {
    return fallback;
  }
}

function readFloat(key, fallback) {
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return fallback;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function writeKey(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Quota / disabled — preference just won't survive the session.
  }
}

export function SoundProvider({ children }) {
  const [muted, setMuted] = useState(() => readBool("mm_muted", false));
  const [volume, setVolume] = useState(() => readFloat("mm_volume", 0.7));
  const engineRef = useRef(null);

  useEffect(() => {
    writeKey("mm_muted", muted);
  }, [muted]);

  useEffect(() => {
    writeKey("mm_volume", volume);
  }, [volume]);

  const getEngine = useCallback(() => {
    if (!engineRef.current) engineRef.current = createAudioEngine();
    return engineRef.current;
  }, []);

  const play = useCallback((soundName) => {
    if (muted) return;
    try {
      const engine = getEngine();
      if (engine[soundName]) engine[soundName]();
      // Haptic feedback on mobile
      if (navigator.vibrate) {
        const patterns = {
          coinClick: [30], cardDraw: [15], meleeImpact: [50, 30, 50],
          winFanfare: [30, 20, 30, 20, 60], failBuzz: [40], playCard: [20],
        };
        if (patterns[soundName]) navigator.vibrate(patterns[soundName]);
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Audio playback failed:", err);
      }
    }
  }, [muted, getEngine]);

  const contextValue = useMemo(
    () => ({ play, muted, setMuted, volume, setVolume }),
    [play, muted, volume],
  );

  return (
    <SoundContext.Provider value={contextValue}>
      {children}
    </SoundContext.Provider>
  );
}
